import React, { useRef, useState, useEffect } from 'react'
import { linkPath, linkPathImageChat } from '../../../share/helper'
import MessageBubble from './MessageBubble'

export default function ChatWindow({ chatTarget, messages, user, messageText, setMessageText, onSend, messagesEndRef, typing, presence = null, isConverting = false, onPrepareFiles = null }) {
  const name = chatTarget ? (chatTarget.fullname || chatTarget.username) : ''
  const fileInputRef = useRef(null)
  const [selectedFiles, setSelectedFiles] = useState([])

  // 🔥 Helper function to get the correct base URL for avatars
  const getAvatarBase = (target) => {
    if (target?.is_group) {
      return linkPathImageChat // Group avatar base
    }
    return linkPath // Private user avatar base
  }

  // 🔥 Helper function to get image URL
  const getImageUrl = (image, baseUrl) => {
    if (!image) return null
    if (String(image).startsWith('http')) return image
    if (String(image).startsWith('/')) return baseUrl + image.replace(/^\//, '')
    return baseUrl + image
  }

  useEffect(() => {
    return () => {
      // revoke object URLs
      selectedFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview) })
    }
  }, [selectedFiles])

  function handleFileClick() {
    // Use a dynamic input element to ensure change fires every time
    const dyn = document.createElement('input')
    dyn.type = 'file'
    dyn.multiple = true
    dyn.onchange = (e) => {
      const files = e.target.files ? Array.from(e.target.files) : []
      appendFiles(files)
      // clean up
      dyn.remove()
    }
    dyn.click()
  }

  function handleFilesChange(e) {
    const files = e.target.files ? Array.from(e.target.files) : []
    appendFiles(files)
    if (fileInputRef.current) fileInputRef.current.value = null
  }

  async function appendFiles(files) {
    let mapped = files.map(f => ({ file: f, preview: f.type && f.type.startsWith('image/') ? URL.createObjectURL(f) : null }))
    if (onPrepareFiles && typeof onPrepareFiles === 'function') {
      try {
        const prepared = await onPrepareFiles(files)
        if (Array.isArray(prepared) && prepared.length) {
          mapped = prepared.map(p => ({ file: p.file, preview: p.preview || (p.file && p.file.type && p.file.type.startsWith('image/') ? URL.createObjectURL(p.file) : null) }))
        }
      } catch (e) {
        console.warn('onPrepareFiles failed', e)
      }
    }
    setSelectedFiles(prev => {
      const prevMap = new Map(prev.map(p => [`${p.file.name}_${p.file.size}`, p]))
      mapped.forEach(m => {
        const key = `${m.file.name}_${m.file.size}`
        if (!prevMap.has(key)) prevMap.set(key, m)
        else {
          if (m.preview) URL.revokeObjectURL(m.preview)
        }
      })
      return Array.from(prevMap.values())
    })
  }

  function removeSelected(index) {
    const toRemove = selectedFiles[index]
    if (toRemove && toRemove.preview) URL.revokeObjectURL(toRemove.preview)
    const next = selectedFiles.slice(0, index).concat(selectedFiles.slice(index+1))
    setSelectedFiles(next)
    if (fileInputRef.current) fileInputRef.current.value = null
  }

  async function handleSend() {
    if (isConverting) return
    const files = selectedFiles && selectedFiles.length ? selectedFiles.map(f => f.file) : null
    await onSend && onSend(files)
    // clear selected files after send
    selectedFiles.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview) })
    setSelectedFiles([])
    if (fileInputRef.current) fileInputRef.current.value = null
  }

  // 🔥 Get the avatar URL for the chat target
  const getAvatarUrl = () => {
    if (!chatTarget) return null
    const baseUrl = getAvatarBase(chatTarget)
    if (chatTarget.avatar) {
      return getImageUrl(chatTarget.avatar, baseUrl)
    }
    return null
  }

  return (
    <div className="chat-window-container">
      <div className="chat-header">
        <div className="chat-header-left">
          <div className="chat-avatar">
            {chatTarget && chatTarget.avatar ? (
              <img
                src={getAvatarUrl()}
                alt={chatTarget.fullname || chatTarget.username}
                style={{width:'100%', height:'100%', objectFit:'cover',fontFamily:'Content'}}
              />
            ) : (name ? name.slice(0,1).toUpperCase() : 'C')}
          </div>
          {/* mobile-only search moved to parent `MyMessageChat` */}
          <div>
           <div className="chat-title" style={{fontFamily:'Content'}}>
  {name}
  {chatTarget?.is_group && (
    <span style={{ 
      fontSize: 10, 
      color: '#1a73e8', 
      marginLeft: 8, 
      background: '#e8f0fe', 
      padding: '2px 10px', 
      borderRadius: 10,
      fontWeight: 'normal'
    }}>
       • Group
    </span>
  )}
</div>
            {chatTarget ? (
              <div className="chat-status">
                {chatTarget?.is_group ? (
                  // 🔥 Group status - show member count
                  <span style={{ color: '#1a73e8' }}>
                    {chatTarget.member_count || 0} members
                  </span>
                ) : (
                  // Private chat status - online/offline
                  presence ? (((presence.ids && presence.ids.has && presence.ids.has(String(chatTarget.id))) || (presence.names && presence.names.has && chatTarget && chatTarget.username && presence.names.has(String(chatTarget.username)))) ? 'Online' : 'Offline') : 'Online'
                )}
              </div>
            ) : null}
          </div>
        </div>
        <div>
          <button className="menu-button">⋯</button>
        </div>
      </div>

        

      <div className="chat-body" id="messages-list">
        {(Array.isArray(messages) ? messages : []).map((m, idx) => {
          const me = user || {}
          const normalizeUserIds = (u) => {
            const s = new Set()
            if (!u) return s
            const keys = ['id','user_id','userId','staff_id','staffId','staffid','userid']
            keys.forEach(k => { if (u[k] !== undefined && u[k] !== null) s.add(String(u[k])) })
            if (u.username) {
              const un = String(u.username)
              s.add(un)
              const nn = Number(un)
              if (Number.isFinite(nn) && nn > 0) s.add(String(nn))
            }
            return s
          }

          const normalizeMsgSenderIds = (msg) => {
            const s = new Set()
            if (!msg) return s
            const keys = ['sender_id','senderId','sender_user_id','sender_userid','senderUserId','from_user','user_id','userId','id']
            keys.forEach(k => { if (msg[k] !== undefined && msg[k] !== null) s.add(String(msg[k])) })
            if (msg.sender && typeof msg.sender === 'object') {
              const sk = ['id','user_id','userId','staff_id','staffId']
              sk.forEach(k => { if (msg.sender[k] !== undefined && msg.sender[k] !== null) s.add(String(msg.sender[k])) })
              if (msg.sender.username) s.add(String(msg.sender.username))
            }
            if (msg.sender_username) s.add(String(msg.sender_username))
            return s
          }

          const myIds = normalizeUserIds(me)
          const senderIds = normalizeMsgSenderIds(m)
          const isMine = Array.from(senderIds).some(id => myIds.has(id)) || (m.sender_username && me.username && String(m.sender_username) === String(me.username))
          const key = (m && m.id) ? `msg-${m.id}` : `msg-tmp-${idx}-${m && m.created_at ? m.created_at : ''}`
          // debug helper: show mapping in console to diagnose ownership mismatches
          if (process.env.NODE_ENV !== 'production') {
            try { console.debug('Message ownership', { id: m && m.id, sender_id: m && m.sender_id, sender_username: m && m.sender_username, computedIsMine: isMine, userSnapshot: me }) } catch(e){}
          }
          return <MessageBubble key={key} m={m} isMine={isMine} />
        })}
        {typing ? <div className="chat-typing">Typing...</div> : null}
        <div ref={messagesEndRef} />
      </div>

      {selectedFiles && selectedFiles.length ? (
        <div className="chat-previews">
          {selectedFiles.map((f, i) => (
            <div key={i} className="file-preview">
              {f.preview ? <img src={f.preview} alt={f.file.name} style={{width:64, height:64, objectFit:'cover', borderRadius:6}} /> : <div className="text-sm">{f.file.name}</div>}
              <button aria-label="Remove file" title="Remove" onClick={() => removeSelected(i)} className="file-remove-btn">✕</button>
            </div>
          ))}
        </div>
      ) : null}

   <div className="chat-input-area" style={{
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 12px',
  backgroundColor: '#fff',
  borderTop: '1px solid #e0e0e0',
  flexWrap: 'wrap',
  minHeight: '50px',
  maxWidth: '100%',
  boxSizing: 'border-box'
}}>
  <input 
    ref={fileInputRef} 
    id="chat-file-input" 
    type="file" 
    multiple 
    style={{display:'none'}} 
    onChange={handleFilesChange} 
  />
  
  <button 
    onClick={handleFileClick} 
    className="chat-attach-btn" 
    disabled={!!isConverting} 
    title={isConverting ? 'Converting...' : 'Attach file'}
    style={{
      padding: '8px 12px',
      backgroundColor: '#f0f0f0',
      border: '1px solid #d9d9d9',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      minWidth: '40px',
      flexShrink: 0
    }}
  >
    📎
  </button>
  
  <input
    id="msg-text"
    value={messageText} 
    onChange={e => setMessageText(e.target.value)}
    onKeyDown={e => { 
      if (e.key === 'Enter') { 
        e.preventDefault(); 
        handleSend();
      } 
    }}
    placeholder="សរសេរ សារ...."
    className="chat-text-input"
    style={{
      flex: '1 1 150px',
      minWidth: '100px',
      padding: '8px 12px',
      border: '1px solid #d9d9d9',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      boxSizing: 'border-box'
    }}
  />
  
  <button 
    onClick={handleSend} 
    className="chat-send-btn" 
    disabled={!!isConverting}
    style={{
   
      padding: '8px 16px',
      backgroundColor: isConverting ? '#d9d9d9' : '#1890ff',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      cursor: isConverting ? 'not-allowed' : 'pointer',
      fontSize: '14px',
      minWidth: '80px',
      flexShrink: 0,
      whiteSpace: 'nowrap'
    }}
  >
    {isConverting ? 'កំពង់ផ្ញើរ...' : 'ផ្ញើសារ'}
  </button>
</div>
    </div>
  )
}